import type { CustomFile } from '../types';

import type { TypeRequestReason } from '../../request/types';
import type { TypeUploadConfig, TypeUploadResult } from './types';
import type { TypeUploadContext } from './createUploadContext';

import throttle from '../../timing/throttle';
import createRequestRegistry from './createRequestRegistry';
import uploadOnceBatch from './uploadOnceBatch';
import resolveUploadConfig from './core/resolveUploadConfig';
import composeAttempt from './core/composeAttempt';
import classifyOutcome from './core/classifyOutcome';
import shouldSend from './core/shouldSend';
import createFeatures from './features';

// ----------------------------------------------------------------------------

/*
 * Batch orchestrator: filters the set once, then sends what is left in a single request.
 * That one outcome is fanned back out to every file, so a single failure marks the whole
 * batch as failed — there is no per-file granularity to recover.
 */
async function uploadBatchFiles(
    files: CustomFile[],
    config: TypeUploadConfig,
    context: TypeUploadContext,
): Promise<TypeUploadResult> {
    const success: TypeUploadResult['success'] = [];
    const error: TypeUploadResult['error'] = [];
    const skipped: TypeUploadResult['skipped'] = [];

    const features = resolveUploadConfig(config);

    const isAborted = (): boolean => context.abortedFlag.aborted;
    const waitForResume = async (): Promise<void> => context.pausedFlag.waitForResume();

    // Same feature set as the per-file path; only what they wrap differs.
    const plugins = createFeatures(features, { isAborted, waitForResume });

    const activeFiles: CustomFile[] = [];
    const unfiltered: CustomFile[] = [];

    for (const file of files) {
        /*
         * Checked per file, the way the per-file worker does: an async filter over a large set
         * would otherwise keep running long after an abort, so cancelling would not cancel.
         * Whatever is left unexamined is reported as never attempted.
         */
        if (context.abortedFlag.aborted) {
            unfiltered.push(file);
            continue;
        }

        if (await shouldSend(plugins, file)) {
            activeFiles.push(file);
        } else {
            context.skippedCount += 1;
            skipped.push({ file });
        }
    }

    /*
     * Only an abort leaves files unreached: the single request covers all of them at once. Files
     * the filter never got to are folded in here so the four buckets still add up to the input.
     */
    const fnResult = (notAttempted: TypeUploadResult['notAttempted'] = []): TypeUploadResult => {
        return {
            success,
            error,
            skipped,
            notAttempted: [...unfiltered.map((file) => ({ file })), ...notAttempted],
            aborted: context.abortedFlag.aborted,
        };
    };

    const totalBytes = activeFiles.reduce((sum, f) => sum + (f.size ?? 0), 0);
    const progressMap = activeFiles.map((f) => ({ file: f, loaded: 0 }));

    let lastPercent = 0;

    const fnEmitProgress = (evt: ProgressEvent | null, totalPercent: number): void => {
        if (!features.progress.enabled) {
            return;
        }

        /*
         * Never regresses: a retry opens a new request whose first progress event reports ~0, and
         * batch mode emits no fnFileRetry, so the caller's bar would jump back with no explanation.
         */
        lastPercent = Math.max(lastPercent, totalPercent);

        config.fnProgressUpload?.(evt, {
            totalPercent: lastPercent,
            files: progressMap.map((p): any => ({ filename: p.file.name, filePercent: p.loaded })),
            currentFile: Math.min(context.completedFiles.value + 1, context.totalFiles),
            errorCount: context.errorCount,
            successCount: context.successCount,
            skippedCount: context.skippedCount,
            totalFiles: context.totalFiles,
        });
    };

    const throttledEmit = throttle(fnEmitProgress, features.progress.throttleMs);

    // Drops any pending throttled call and emits the final state immediately.
    const fnFlushProgress = (totalPercent: number): void => {
        throttledEmit.cancel();
        fnEmitProgress(null, totalPercent);
    };

    /*
     * Marks every file as terminally processed (success or definitive error) so the final
     * progress reads 100% instead of stalling on whatever the last upload event carried.
     */
    const fnSettleProgress = (): void => {
        for (const p of progressMap) {
            p.loaded = 100;
        }

        context.completedFiles.value = context.totalFiles;
        fnFlushProgress(100);
    };

    /*
     * Nothing left to send: every file was filtered out, or an abort cut the filter short. The
     * caller still gets one terminal event, or a UI driven purely by fnProgressUpload would sit
     * at its last value forever. An abort keeps whatever was reached; otherwise every file is
     * terminally processed, so 100%.
     */
    if (activeFiles.length === 0) {
        if (context.abortedFlag.aborted) {
            fnFlushProgress(lastPercent);
        } else {
            fnSettleProgress();
        }

        return fnResult();
    }

    const registry = createRequestRegistry(context);

    context.abortAll = registry.abortAll;

    // ------------------------------------------------------------------------

    const attempt = composeAttempt(
        async () =>
            uploadOnceBatch(activeFiles, {
                config,
                features,
                plugins,
                context,
                registry,
                progressMap,
                totalBytes,
                fnEmitProgress: throttledEmit,
            }),
        plugins,
        { isAborted, waitForResume },
    );

    const result = await attempt();
    const outcome = classifyOutcome(result, isAborted, (xhr) => registry.isAborted(xhr));

    const lastStatus = result.status ?? 0;
    const lastReason: TypeRequestReason = result.reason;
    const lastMessage = result.message;

    if (outcome === 'ok') {
        for (const file of activeFiles) {
            context.successCount++;
            success.push({ file, xhr: result.xhr });
            config.fnFileSuccess?.(file, result.xhr);
        }

        fnSettleProgress();
        return fnResult();
    }

    if (outcome === 'aborted') {
        fnFlushProgress(lastPercent);
        return fnResult(activeFiles.map((file) => ({ file })));
    }

    for (const file of activeFiles) {
        context.errorCount++;

        const statusMsg =
            lastStatus && lastReason !== 'abort'
                ? `Upload ${lastReason.toUpperCase()} (HTTP ${lastStatus})`
                : `Upload ${lastReason.toUpperCase()}`;
        const msg = lastMessage ?? statusMsg;

        error.push({ file, err: msg });
        config.fnFileError?.(file, msg);
    }

    fnSettleProgress();

    return fnResult();
}

// ----------------------------------------------------------------------------

export default uploadBatchFiles;
